import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocation, RouteLocationRaw, RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router'
import { setDocumentTitle, formatRouteName } from '@/utils/title'
import { PERMISSIONS, PMO_WEEKLY_CHECK_IN_ALL_PERMISSIONS, PMO_COORDINATOR_FORM_ANY_PERMISSIONS } from '@/constants/permissions'
import { dashboardPageTitle, isDashboardPageVisible } from '@/constants/dashboardPages'
import { uuidEquals } from '@/utils/uuidNormalize'
import { canAccessWeeklyCheckInModuleFromStore, isWeeklyCheckInModuleNavigation } from '@/utils/weeklyCheckInRouteAccess'
import { userHasCompletedTwoFactorSetup } from '@/utils/userTwoFactor'
import {
  isChunkLoadError,
  persistChunkRecoveryTargetAndReload,
  showChunkRecoveryPendingToastIfAny,
} from '@/utils/chunkLoadRecovery'
import { syncDocumentThemeFromStorage } from '@/utils/documentTheme'
import { installReleaseNavigationGuard } from '@/router/releaseNavigationGuard'
import { useAuthStore } from '@/stores/auth'
import { handleSalesDashboardNavigation } from '@/stores/salesDashboardPages'
import { useDdQueueStore } from '@/stores/ddQueueStore'
import {
  getFirstAccessibleSalesDashboardSubPageRouteName,
  getFirstAccessibleSalesRoute,
  LEGACY_SALES_VIEW_PERMISSIONS,
  salesDashboardViewPermissionOptions,
  salesViewPermissionOptions,
} from '@/utils/salesNavigationAccess'
import { SW_TILE_CREATE_PERMISSIONS } from '@/utils/swDdAccess'
import {
  hasAnyNavigationItemPermission,
  hasNavigationItemPermission,
} from '@/utils/navigationPermissionAccess'

/** Last navigation target — used when a lazy chunk fails so reload can restore the clicked route. */
let chunkRecoveryPendingFullPath: string | null = null

// Helper function to wrap lazy imports with error handling and retry logic
/** Preserve query string and hash when redirecting legacy PIM URLs. */
function pimRedirect(path: string): RouteRecordRedirectOption {
  return (to: RouteLocation): RouteLocationRaw => ({
    path,
    query: to.query,
    hash: to.hash,
  })
}

const createLazyComponent = (importFn: () => Promise<any>, componentName: string) => {
  return () =>
    importFn().catch((error) => {
      console.error(`Failed to load component ${componentName}:`, error)
      // Re-throw so vue-router onError runs once (single recovery path; no hung navigation).
      throw error
    })
}

const routes: RouteRecordRaw[] = [
  // Public routes (no layout)
  {
    path: '/',
    name: 'Welcome',
    component: () => import('@/views/WelcomeView.vue'),
    meta: { title: 'Welcome' }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: 'Login' }
  },
  {
    path: '/two-factor-challenge',
    name: 'TwoFactorChallenge',
    component: () => import('@/views/auth/TwoFactorVerifyView.vue'),
    meta: { title: 'Two-Factor Authentication' }
  },
  // Amazon routes removed - components kept but routes disabled
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { title: 'Register' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { title: 'Forgot Password' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { title: 'Reset Password' }
  },
  {
    path: '/public/vendors/enroll',
    name: 'VendorEnroll',
    component: () => import('@/views/public/VendorEnrollView.vue'),
    meta: { title: 'Vendor Enrollment' }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/public/PrivacyPolicyView.vue'),
    meta: { title: 'Privacy Policy' }
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: () => import('@/views/public/TermsAndConditionsView.vue'),
    meta: { title: 'Terms and Conditions' }
  },

  // Authenticated routes (with AppLayout - includes sidebar)
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/auth/LoggedInWelcomeView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Home' } // Personal route - no permission required
  },
  {
    path: '/project-management/dashboard',
    name: 'ProjectManagementDashboard',
    component: () => import('@/views/project-management/ProjectManagementPlaceholderView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [...PMO_WEEKLY_CHECK_IN_ALL_PERMISSIONS],
      title: 'Project Management',
    },
  },
  {
    path: '/project-management/weekly-check-in',
    name: 'WeeklyCheckIn',
    component: () => import('@/views/project-management/WeeklyCheckInView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [...PMO_WEEKLY_CHECK_IN_ALL_PERMISSIONS],
      title: 'Weekly Check-In'
    }
  },
  {
    path: '/project-management/weekly-check-in/dashboard',
    name: 'WeeklyCheckInDashboard',
    component: () => import('@/views/project-management/WeeklyCheckInDashboardView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [PERMISSIONS.PMO.DASHBOARD.VIEW, PERMISSIONS.PMO.DASHBOARD.VIEW_ALL],
      title: 'Weekly Check-In Dashboard'
    }
  },
  {
    path: '/project-management/weekly-check-in/dashboard/print',
    name: 'WeeklyCheckInDashboardPrint',
    component: () => import('@/views/project-management/WeeklyCheckInDashboardPrintView.vue'),
    meta: {
      layout: 'print',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [PERMISSIONS.PMO.DASHBOARD.VIEW, PERMISSIONS.PMO.DASHBOARD.VIEW_ALL],
      title: 'Weekly Check-In — Dashboard print'
    }
  },
  {
    path: '/project-management/pmo-admin',
    name: 'ProjectManagementPmoAdmin',
    component: () => import('@/views/project-management/WeeklyCheckInPmoAdminView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [
        PERMISSIONS.PMO.ADMIN_FORM.VIEW,
        PERMISSIONS.PMO.ADMIN_FORM.CREATE,
        PERMISSIONS.PMO.ADMIN_FORM.VIEW_ALL
      ],
      title: 'PMO Admin'
    }
  },
  {
    path: '/project-management/team-leader',
    name: 'ProjectManagementTeamLeader',
    component: () => import('@/views/project-management/WeeklyCheckInTeamLeaderView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [
        PERMISSIONS.PMO.LEADER_FORM.VIEW,
        PERMISSIONS.PMO.LEADER_FORM.CREATE,
        PERMISSIONS.PMO.LEADER_FORM.VIEW_ALL
      ],
      title: 'Team Leader'
    }
  },
  {
    path: '/project-management/weekly-check-in/coordinator/:userUuid',
    name: 'WeeklyCheckInCoordinator',
    component: () => import('@/views/project-management/WeeklyCheckInCoordinatorView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [...PMO_COORDINATOR_FORM_ANY_PERMISSIONS],
      title: 'Weekly Check-In'
    }
  },
  {
    path: '/project-management/weekly-check-in/print/:uuid',
    name: 'WeeklyCheckInPrint',
    component: () => import('@/views/project-management/WeeklyCheckInPrintView.vue'),
    meta: {
      layout: 'print',
      requiresAuth: true,
      weeklyCheckInModule: true,
      requiredPermissions: [...PMO_WEEKLY_CHECK_IN_ALL_PERMISSIONS],
      title: 'Weekly Check-In — Print'
    }
  },
  {
    path: '/product-dev/dashboard',
    name: 'product-dev-dashboard',
    component: () => import('@/views/pim/ProductDevelopmentDashboardView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [PERMISSIONS.PIM.PRODUCTS.VIEW, PERMISSIONS.PIM.CATEGORIES.VIEW],
      title: 'Product Development Dashboard'
    }
  },
  {
    path: '/product-dev/pim/new',
    name: 'product-create',
    component: () => import('@/views/pim/ProductEditView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.PRODUCTS.CREATE, title: 'Create Product' }
  },
  {
    path: '/product-dev/pim/:uuid/edit',
    name: 'product-edit',
    component: () => import('@/views/pim/ProductEditView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.PRODUCTS.EDIT, title: 'Edit Product' }
  },
  {
    path: '/product-dev/pim/:uuid',
    name: 'product-detail',
    component: () => import('@/views/pim/ProductDetailView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.PRODUCTS.VIEW, title: 'Product Details' }
  },
  {
    path: '/product-dev/pim',
    name: 'products',
    component: () => import('@/views/pim/ProductsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.PRODUCTS.VIEW, title: 'Products' }
  },
  {
    path: '/product-dev/forms/universal-intake/legacy-wizard',
    name: 'ProductDevFormLegacyWizard',
    component: () => import('@/views/product-dev-form/ProductDevFormWizardView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.PIM.UNIVERSAL_INTAKE.CREATE,
      title: 'Universal Intake Form (legacy wizard)'
    }
  },
  {
    path: '/product-dev/forms/universal-intake',
    name: 'ProductDevFormNew',
    component: () => import('@/views/product-dev-form/ProductDevFormUniversalWizardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.UNIVERSAL_INTAKE.CREATE, title: 'Universal Intake Form' }
  },
  {
    path: '/product-dev/lists/universal-intake/:uuid/edit',
    name: 'ProductDevFormEdit',
    component: () => import('@/views/product-dev-form/ProductDevFormEditRouterView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.PIM.UNIVERSAL_INTAKE.EDIT, title: 'Edit Product Dev Form' }
  },
  {
    path: '/product-dev/lists/universal-intake/:uuid',
    name: 'ProductDevFormDetail',
    component: () => import('@/views/product-dev-form/ProductDevFormDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [PERMISSIONS.PIM.UNIVERSAL_INTAKE.VIEW, PERMISSIONS.PIM.UNIVERSAL_INTAKE.VIEW_ALL],
      title: 'Product Dev Form Details'
    }
  },
  {
    path: '/product-dev/lists/universal-intake',
    name: 'ProductDevFormList',
    component: () => import('@/views/product-dev-form/ProductDevFormListView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [PERMISSIONS.PIM.UNIVERSAL_INTAKE.VIEW, PERMISSIONS.PIM.UNIVERSAL_INTAKE.VIEW_ALL],
      title: 'Universal Intake List'
    }
  },
  {
    path: '/pdq/form',
    name: 'PdqForm',
    component: () => import('@/views/print-bridge/PrintBridgeFormView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.PIM.PRINT_BRIDGE.CREATE,
      title: 'PDQ Form'
    }
  },
  {
    path: '/pdq/form/request-a-design',
    name: 'PdqRequestADesignForm',
    component: () => import('@/views/print-bridge/PdqRequestADesignFormView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.PIM.PRINT_BRIDGE.CREATE,
      title: 'Request A Design'
    }
  },
  {
    path: '/pdq/form/design-job',
    redirect: (to): RouteLocationRaw => ({
      path: '/pdq/form/request-a-design',
      query: to.query,
      hash: to.hash
    })
  },
  {
    path: '/pdq/list',
    name: 'PdqList',
    component: () => import('@/views/print-bridge/PrintBridgeListView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [PERMISSIONS.PIM.PRINT_BRIDGE.VIEW, PERMISSIONS.PIM.PRINT_BRIDGE.VIEW_ALL],
      title: 'PDQ List'
    }
  },
  {
    path: '/pdq/list/:uuid',
    name: 'PdqDetail',
    component: () => import('@/views/print-bridge/PrintBridgeDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [PERMISSIONS.PIM.PRINT_BRIDGE.VIEW, PERMISSIONS.PIM.PRINT_BRIDGE.VIEW_ALL],
      title: 'PDQ Details'
    }
  },
  { path: '/print-bridge/form', redirect: '/pdq/form' },
  { path: '/print-bridge/form/design-job', redirect: '/pdq/form/request-a-design' },
  { path: '/print-bridge/form/request-a-design', redirect: '/pdq/form/request-a-design' },
  { path: '/print-bridge/list', redirect: '/pdq/list' },
  {
    path: '/print-bridge/list/:uuid',
    redirect: (to): RouteLocationRaw => ({ path: `/pdq/list/${String(to.params.uuid)}`, query: to.query, hash: to.hash })
  },

  // Legacy PIM / Universal Intake URLs (preserve query + hash)
  { path: '/pim/products/new', redirect: pimRedirect('/product-dev/pim/new') },
  // NOTIF-011 C3 — the export download page is gone: the file comes through the notification.
  { path: '/pim/exports/download', redirect: pimRedirect('/notifications') },
  { path: '/product-dev/pim/exports/download', redirect: pimRedirect('/notifications') },
  {
    path: '/pim/products/:uuid/edit',
    redirect: (to): RouteLocationRaw => ({
      path: `/product-dev/pim/${to.params.uuid}/edit`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: '/pim/products/:uuid',
    redirect: (to): RouteLocationRaw => ({
      path: `/product-dev/pim/${to.params.uuid}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  { path: '/pim/products', redirect: pimRedirect('/product-dev/pim') },
  { path: '/pim/product-dev-forms/new', redirect: pimRedirect('/product-dev/forms/universal-intake') },
  {
    path: '/pim/product-dev-forms/legacy-wizard',
    redirect: pimRedirect('/product-dev/forms/universal-intake/legacy-wizard')
  },
  { path: '/pim/product-dev-forms', redirect: pimRedirect('/product-dev/lists/universal-intake') },
  {
    path: '/pim/product-dev-forms/:uuid/edit',
    redirect: (to): RouteLocationRaw => ({
      path: `/product-dev/lists/universal-intake/${to.params.uuid}/edit`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: '/pim/product-dev-forms/:uuid',
    redirect: (to): RouteLocationRaw => ({
      path: `/product-dev/lists/universal-intake/${to.params.uuid}`,
      query: to.query,
      hash: to.hash,
    }),
  },

  // CRM Routes
  {
    path: '/crm',
    name: 'CRM',
    component: () => import('@/views/crm/CRMView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.ACCOUNTS.VIEW, title: 'CRM Dashboard' }
  },
  {
    path: '/crm/accounts',
    name: 'CRMAccounts',
    component: () => import('@/views/crm/AccountsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.ACCOUNTS.VIEW, title: 'Accounts' }
  },
  {
    path: '/crm/accounts/create',
    name: 'CRMCreateAccount',
    component: () => import('@/views/crm/CreateAccountView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.ACCOUNTS.CREATE, title: 'Create Account' }
  },
  {
    path: '/crm/accounts/:uuid',
    name: 'CRMAccountDetails',
    component: () => import('@/views/crm/AccountDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.ACCOUNTS.VIEW, title: 'Account Details' }
  },
  {
    path: '/crm/accounts/:uuid/edit',
    name: 'CRMEditAccount',
    component: () => import('@/views/crm/EditAccountView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.ACCOUNTS.EDIT, title: 'Edit Account' }
  },
  
  // Design Jobs Routes
  {
    path: '/design-jobs/queues',
    name: 'DesignJobsQueue',
    component: createLazyComponent(() => import('@/views/design-jobs/DesignJobsQueueView.vue'), 'DesignJobsQueueView'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.DESIGN.QUEUE.VIEW, title: 'Design Jobs Queue' }
  },
  {
    path: '/design-jobs/completed',
    name: 'DesignJobsCompleted',
    component: createLazyComponent(() => import('@/views/design-jobs/DesignJobsCompletedView.vue'), 'DesignJobsCompletedView'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: 'design.completed.view', title: 'Completed Design Jobs' }
  },
  {
    path: '/design-jobs/create',
    name: 'DesignJobCreate',
    component: () => import('@/views/design-jobs/DesignJobCreateView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.DESIGN.QUEUE.CREATE, title: 'Create Design Job' }
  },
  {
    path: '/design-jobs/:uuid',
    name: 'DesignJobDetail',
    component: () => import('@/views/design-jobs/DesignJobDetailView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Design Job Detail' } // Personal route - no permission required for now
  },
  {
    path: '/design-jobs/:uuid/edit',
    name: 'DesignJobEdit',
    component: () => import('@/views/design-jobs/DesignJobEditView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.DESIGN.QUEUE.EDIT, title: 'Edit Design Job' }
  },
  {
    path: '/crm/sub-accounts',
    name: 'CRMSubAccounts',
    component: () => import('@/views/crm/SubAccountsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.SUBACCOUNTS.VIEW, title: 'Sub-Accounts' }
  },
  {
    path: '/crm/sub-accounts/create',
    name: 'CRMCreateSubAccount',
    component: () => import('@/views/crm/CreateSubAccountView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.SUBACCOUNTS.CREATE, title: 'Create Sub-Account' }
  },
  {
    path: '/crm/sub-accounts/:uuid',
    name: 'CRMSubAccountDetails',
    component: () => import('@/views/crm/SubAccountDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.SUBACCOUNTS.VIEW, title: 'Sub-Account Details' }
  },
  {
    path: '/crm/sub-accounts/:uuid/edit',
    name: 'CRMEditSubAccount',
    component: () => import('@/views/crm/EditSubAccountView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.SUBACCOUNTS.EDIT, title: 'Edit Sub-Account' }
  },
  {
    path: '/crm/clients',
    name: 'CRMClients',
    component: () => import('@/views/crm/ClientsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.CONTACTS.VIEW, title: 'Contacts' }
  },
  {
    path: '/crm/clients/:uuid',
    name: 'CRMClientDetails',
    component: () => import('@/views/crm/ClientDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.CONTACTS.VIEW, title: 'Contact Details' }
  },
  {
    path: '/crm/clients/:uuid/edit',
    name: 'CRMEditClient',
    component: () => import('@/views/crm/EditClientView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.CONTACTS.EDIT, title: 'Edit Contact' }
  },
  {
    path: '/crm/projects',
    name: 'CRMProjects',
    component: () => import('@/views/crm/ProjectsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.PROJECTS.VIEW, title: 'Projects' }
  },
  {
    path: '/crm/projects/create',
    name: 'CRMCreateProject',
    component: () => import('@/views/crm/CreateProjectView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.PROJECTS.CREATE, title: 'Create Project' }
  },
  {
    path: '/crm/projects/:uuid',
    name: 'CRMProjectDetails',
    component: () => import('@/views/crm/ProjectDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.PROJECTS.VIEW, title: 'Project Details' }
  },
  {
    path: '/crm/projects/:uuid/edit',
    name: 'CRMEditProject',
    component: () => import('@/views/crm/EditProjectView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.PROJECTS.EDIT, title: 'Edit Project' }
  },
  {
    path: '/crm/notes/:id',
    name: 'CRMNoteDetails',
    component: () => import('@/views/crm/NoteDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.NOTES.VIEW, title: 'Note Details' }
  },
  {
    path: '/sales/flash-reports/:id',
    name: 'FlashReportDetails',
    component: () => import('@/views/crm/NoteDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.RD.FLASHREPORTS.VIEW, title: 'Flash Report Details' }
  },
  {
    path: '/my/notes/:id',
    name: 'MyNoteDetails',
    component: () => import('@/views/crm/NoteDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Note Details' } // Personal route - no permission required
  },
  {
    path: '/my/tasks/:uuid',
    name: 'MyTaskDetails',
    component: () => import('@/views/crm/TaskDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Task Details' } // Personal route - no permission required
  },
  {
    path: '/crm/tasks/:uuid/edit',
    name: 'CRMTaskEdit',
    component: () => import('@/views/crm/EditTaskView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.TASKS.EDIT, title: 'Edit Task' }
  },
  {
    path: '/crm/tasks/:uuid',
    name: 'CRMTaskDetails',
    component: () => import('@/views/crm/TaskDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.CRM.TASKS.VIEW, title: 'Task Details' }
  },
  
  // Reporting Dashboards Routes
  {
    path: '/reporting-dashboards',
    name: 'ReportingDashboards',
    component: () => import('@/views/reporting-dashboards/DashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.RD.TIMEPHASES.VIEW, title: 'Reporting Dashboards' }
  },
  {
    path: '/sales',
    component: () => import('@/views/sales/dashboard-pages/SalesDashboardSharedLayout.vue'),
    meta: { layout: 'sidebar', requiresAuth: true },
    children: [
      {
        path: '',
        redirect: () => {
          const authStore = useAuthStore()
          const routeName = getFirstAccessibleSalesDashboardSubPageRouteName((permission) =>
            authStore.hasPermission(permission)
          )
          if (routeName) {
            return { name: routeName }
          }
          return getFirstAccessibleSalesRoute((permission) => authStore.hasPermission(permission)) ?? '/home'
        },
      },
      {
        path: 'dashboard',
        name: 'SalesDashboard',
        component: () => import('@/views/sales/dashboard-pages/PerformanceView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermissions: salesDashboardViewPermissionOptions(),
          title: 'Sales Dashboard',
          sdPageSlug: 'performance',
        },
      },
      {
        path: 'leaderboard',
        name: 'SalesLeaderboard',
        component: () => import('@/views/sales/dashboard-pages/LeaderboardView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermission: PERMISSIONS.SALES.LEADERBOARD.VIEW,
          title: 'Leaderboard',
          sdPageSlug: 'leaderboard',
        },
      },
      {
        path: 'open-pipeline',
        name: 'SalesOpenPipeline',
        component: () => import('@/views/sales/dashboard-pages/OpenPipelineView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermissions: salesViewPermissionOptions(
            PERMISSIONS.SALES.OPEN_PIPELINE.VIEW,
            LEGACY_SALES_VIEW_PERMISSIONS.openPipelineView
          ),
          title: 'Open Pipeline',
          sdPageSlug: 'open-pipeline',
        },
      },
      {
        path: 'sale-detail',
        name: 'SalesSaleDetail',
        component: () => import('@/views/sales/dashboard-pages/SaleDetailView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermissions: salesViewPermissionOptions(
            PERMISSIONS.SALES.SALES_DETAIL.VIEW,
            LEGACY_SALES_VIEW_PERMISSIONS.salesDetailView
          ),
          title: 'Sales Detail',
          sdPageSlug: 'sale-detail',
        },
      },
      {
        path: 'amazon-dashboard',
        name: 'SalesAmazonDashboard',
        component: () => import('@/views/sales/dashboard-pages/AmazonDashboardView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermissions: salesViewPermissionOptions(
            PERMISSIONS.SALES.SALES_DETAIL.VIEW,
            LEGACY_SALES_VIEW_PERMISSIONS.salesDetailView
          ),
          title: 'Amazon Dashboard',
          sdPageSlug: 'amazon-dashboard',
        },
      },
      {
        path: 'inbound-orders',
        name: 'SalesInboundOrders',
        component: () => import('@/views/sales/dashboard-pages/InboundOrdersView.vue'),
        meta: {
          requiresAuth: true,
          requiredPermission: PERMISSIONS.SALES.INBOUND_ORDERS.VIEW,
          title: 'Inbound Quotes/Jobs',
          sdPageSlug: 'inbound-orders',
        },
      },
    ],
  },
  {
    path: '/sales/trends',
    name: 'SalesTrends',
    component: () => import('@/views/sales/SalesTrendsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SALESHISTORY.TRENDS.VIEW,
      title: 'Trends',
    },
  },
  {
    path: '/sales/trends/wip-details',
    name: 'SalesTrendsWipDetails',
    component: () => import('@/views/sales/SalesTrendsWipDetailsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SALESHISTORY.TRENDS.VIEW,
      title: 'WIP status details',
    },
  },
  {
    path: '/sales/amazon-sales',
    name: 'SalesAmazonSales',
    component: () => import('@/views/sales/dashboard-pages/AmazonSalesView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: salesDashboardViewPermissionOptions(),
      title: 'Amazon Sales',
    },
  },
  {
    path: '/sales/texas-sales-by-rep',
    name: 'SalesTexasByRep',
    component: () => import('@/views/sales/dashboard-pages/TexasSalesByRepView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SALES.LEADERBOARD.VIEW,
      title: 'Texas — sales by rep',
    },
  },
  {
    path: '/sales/amazon-invoiced-by-state',
    name: 'SalesAmazonByState',
    component: () => import('@/views/sales/dashboard-pages/AmazonInvoicedByStateView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: salesDashboardViewPermissionOptions(),
      title: 'Amazon — invoiced by state',
    },
  },
  {
    path: '/sales/budget',
    redirect: '/sales/budgets',
  },
  {
    path: '/sales/budgets',
    name: 'SalesBudgets',
    component: () => import('@/views/sales/dashboard-pages/BudgetsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SALES.BUDGETS.VIEW,
      title: 'Budgets',
    },
  },
  {
    path: '/sales/teams-and-reps',
    name: 'SalesTeamsAndReps',
    component: () => import('@/views/sales/dashboard-pages/TeamsAndRepsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SALES.TEAMS_REPS.VIEW,
      title: 'Teams & Reps',
    },
  },
  {
    path: '/sales/workflow/new-product',
    name: 'SalesWorkflowNewProduct',
    component: () => import('@/views/sales/workflow/NewProductView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.VIEW,
      title: 'New Product',
    },
  },
  {
    path: '/sales/workflow/modify',
    name: 'SalesWorkflowModify',
    component: () => import('@/views/sales/workflow/ModifyProductView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_MODIFY.VIEW,
      title: 'Modify Existing Product',
    },
  },
  {
    path: '/sales/workflow/product-bundle',
    name: 'SalesWorkflowProductBundle',
    component: () => import('@/views/sales/workflow/ProductBundleView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.VIEW,
      title: 'Product Bundle',
    },
  },
  {
    path: '/sales/workflow/new-customer-order',
    name: 'SalesWorkflowNewCustomerOrder',
    component: () => import('@/views/sales/workflow/NewCustomerOrderView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.VIEW,
      title: 'New Customer Order',
    },
  },
  {
    path: '/sales/workflow/drafts',
    name: 'SalesWorkflowDrafts',
    component: () => import('@/views/sales/workflow/SalesWorkflowDraftsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermissions: [...SW_TILE_CREATE_PERMISSIONS],
      title: 'My Drafts',
    },
  },
  {
    path: '/sales/workflow/submitted',
    name: 'SalesWorkflowSubmitted',
    component: () => import('@/views/sales/workflow/SalesWorkflowSubmittedView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_SUBMITTED.VIEW,
      title: 'Submitted Requests',
    },
  },
  {
    path: '/sales/workflow/submitted/:uuid',
    name: 'SalesWorkflowSubmissionDetail',
    component: () => import('@/views/sales/workflow/SalesWorkflowSubmissionDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      salesWorkflow: true,
      requiredPermission: PERMISSIONS.SALES.WORKFLOW_SUBMITTED.VIEW,
      title: 'Submission detail',
    },
  },
  {
    path: '/design-development/queue',
    name: 'DdQueue',
    component: () => import('@/views/design-development/DdQueueView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      designDevelopment: true,
      requiredPermission: PERMISSIONS.PRODUCTS_DESIGN.QUEUE.VIEW,
      title: 'Design & Development Queue',
    },
  },
  {
    path: '/design-development/workload',
    name: 'DdWorkload',
    component: () => import('@/views/design-development/DdWorkloadView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      designDevelopment: true,
      requiredPermission: PERMISSIONS.PRODUCTS_DESIGN.SUPERVISOR.VIEW,
      title: 'Workload',
    },
  },
  {
    path: '/design-development/dashboard',
    name: 'DdDashboard',
    component: () => import('@/views/design-development/DdDashboardView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      designDevelopment: true,
      requiredPermission: PERMISSIONS.PRODUCTS_DESIGN.QUEUE.VIEW,
      title: 'D&D Dashboard',
    },
  },
  {
    path: '/design-development/history',
    name: 'DdHistory',
    component: () => import('@/views/design-development/DdHistoryView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      designDevelopment: true,
      requiredPermission: PERMISSIONS.PRODUCTS_DESIGN.QUEUE.VIEW,
      title: 'History',
    },
  },
  {
    path: '/design-development/cancelled',
    name: 'DdCancelled',
    component: () => import('@/views/design-development/DdCancelledView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      designDevelopment: true,
      requiredPermission: PERMISSIONS.PRODUCTS_DESIGN.QUEUE.VIEW,
      title: 'Cancelled',
    },
  },
  {
    path: '/sales/customer-mapping',
    name: 'SalesCustomerMapping',
    component: () => import('@/views/sales/dashboard-pages/CustomerMappingView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: salesDashboardViewPermissionOptions(),
      title: 'Customer Mapping',
    },
  },
  {
    path: '/sales/custom-reports',
    name: 'SalesCustomReports',
    component: () => import('@/views/sales/dashboard-pages/CustomReportsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: salesDashboardViewPermissionOptions(),
      title: 'Custom Reports',
    },
  },
  {
    path: '/sales/flash-reports',
    name: 'FlashReports',
    component: () => import('@/views/reporting-dashboards/FlashReportsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.RD.FLASHREPORTS.VIEW, title: 'Flash Reports' }
  },
  {
    path: '/sales/sales-history',
    name: 'SalesHistory',
    component: () => import('@/views/sales/SalesHistoryList.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SALESHISTORY.VIEW, title: 'Sales History' }
  },
  {
    // `?request=<uuid>` opens that request's modal on load.
    path: '/sales/customer-setup',
    name: 'SalesCustomerSetup',
    component: () => import('@/views/sales/CustomerSetupView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SALES.CUSTOMER_SETUP.VIEW, title: 'Customer Form' }
  },
  {
    path: '/sales/customer-setup/settings',
    name: 'SalesCustomerSetupSettings',
    component: () => import('@/views/sales/CustomerSetupSettingsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SALES.CUSTOMER_SETUP_SETTINGS.VIEW, title: 'Customer Form Settings' }
  },
  {
    path: '/sales/performance',
    redirect: '/sales/dashboard',
  },
  {
    path: '/sales/:page',
    name: 'SalesDashboardPageStub',
    component: () => import('@/views/sales/SalesDashboardPageStubView.vue'),
    // Stub pages: accessible to any authenticated user (no permission). `personal: true`
    // satisfies the router guard's auth-only fail-safe. Real pages/permissions added later.
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Dashboard Pages' }
  },
  {
    path: '/sales/dashboard-pages',
    redirect: '/sales',
  },
  {
    path: '/sales/dashboard-pages/:subPath+',
    redirect: (to) => `/sales/${String(to.params.subPath)}`,
  },
  {
    path: '/sales-history/list',
    redirect: '/sales/sales-history',
  },
  {
    path: '/sales-history',
    redirect: '/sales/sales-history',
  },
  {
    path: '/analytics/dwell-time',
    name: 'DwellTime',
    component: () => import('@/views/analytics/DwellTimeView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ANALYTICS.DWELL_TIME.VIEW, title: 'Dwell Time' }
  },
  {
    path: '/analytics/systems-uptime',
    name: 'SystemsUptime',
    component: () => import('@/views/analytics/GrafanaDashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ANALYTICS.SYSTEMS_UPTIME.VIEW, title: 'Systems Uptime', grafanaDashboard: 'systems-uptime' }
  },
  {
    path: '/zoom/dashboard',
    name: 'ZoomDashboard',
    component: () => import('@/views/zoom/ZoomDashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermissions: [PERMISSIONS.ZOOM.DASHBOARD.VIEW, PERMISSIONS.ZOOM.DASHBOARD.EDIT], title: 'Call Intelligence' }
  },
  {
    path: '/quality/claims',
    name: 'Claims',
    component: () => import('@/views/quality/ClaimsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.QUALITY.CLAIMS.VIEW, title: 'Claims' }
  },
  {
    // Deep link used by the quality sheet / emails: opens the claim modal on load.
    path: '/quality/claims/:uuid',
    name: 'ClaimDetail',
    component: () => import('@/views/quality/ClaimsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.QUALITY.CLAIMS.VIEW, title: 'Claims' }
  },
  {
    path: '/quality/claims/:uuid/attachments',
    name: 'ClaimAttachments',
    component: () => import('@/views/quality/ClaimsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.QUALITY.CLAIMS.VIEW, title: 'Claim attachments' }
  },
  {
    path: '/reporting-dashboards/notes',
    name: 'ReportingNotes',
    component: () => import('@/views/reporting-dashboards/DashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.RD.TIMEPHASES.VIEW, title: 'Reporting Notes' }
  },
  {
    path: '/reporting-dashboards/demand-timephase',
    name: 'DemandTimephase',
    component: () => import('@/views/reporting-dashboards/DemandTimephaseView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.OPERATIONS.REPORTS_DEMAND.VIEW, title: 'Demand Timephase' }
  },
  {
    path: '/reporting-dashboards/estimates',
    name: 'Estimates',
    component: () => import('@/views/reporting-dashboards/DashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.RD.ESTIMATES.VIEW, title: 'Estimates' }
  },
  {
    path: '/reporting-dashboards/inquiries',
    name: 'Inquiries',
    component: () => import('@/views/UnderConstructionView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.INQUIRIES.VIEW, title: 'Inquiries' }
  },
  
  // Machines Routes
  {
    path: '/machines/dashboard',
    name: 'MachinesDashboard',
    component: () => import('@/views/machines/MachinesDashboard.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.MACHINES.VIEW, title: 'Machines Dashboard' }
  },
  {
    path: '/machines/jobs',
    name: 'JobsDashboard',
    component: () => import('@/views/machines/JobsDashboard.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.OPERATIONS.JOBS.VIEW, title: 'Jobs Dashboard' }
  },
  {
    path: '/operations/reports/on-time-shipping',
    name: 'OnTimeShipping',
    component: () => import('@/views/operations/OnTimeShippingView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.OPERATIONS.ON_TIME_SHIPPING.VIEW, title: 'On Time Shipping' }
  },
  {
    path: '/durst',
    name: 'DurstPrinter',
    component: () => import('@/views/UnderConstructionView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.MACHINES.VIEW, title: 'Durst Printer' }
  },
  
  // Organization (legacy path → org chart)
  {
    path: '/organization',
    redirect: '/org-chart',
  },

  // Org Chart — personal route for all authenticated internal users
  {
    path: '/org-chart',
    name: 'OrgChart',
    component: () => import('@/views/OrgChartView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Organization Chart' },
  },
  
  // User Routes
  {
    path: '/my/projects',
    name: 'MyProjects',
    component: () => import('@/views/projects/MyProjectsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'My Projects' } // Personal route - no permission required
  },
  {
    path: '/my/projects/create',
    name: 'CreateMyProject',
    component: () => import('@/views/projects/CreateMyProjectView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Create Project' } // Personal route - no permission required
  },
  {
    path: '/my/projects/:uuid',
    name: 'MyProjectDetails',
    component: () => import('@/views/projects/MyProjectDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Project Details' } // Personal route - no permission required
  },
  {
    path: '/my/projects/:uuid/edit',
    name: 'EditMyProject',
    component: () => import('@/views/projects/CreateMyProjectView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Edit Project' } // Personal route - no permission required
  },
  {
    path: '/my/notes',
    name: 'MyNotes',
    component: () => import('@/views/notes/NotesView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'My Notes' } // Personal route - no permission required
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/UserManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.USERS.VIEW, title: 'User Management' }
  },
  {
    path: '/admin/invites',
    name: 'AdminInvites',
    component: () => import('@/views/admin/AdminInvitesView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.INVITES.VIEW, title: 'Invite Management' }
  },
  {
    path: '/bug-reports',
    name: 'BugReports',
    component: () => import('@/views/bug-reports/BugReportsManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.BUGREPORTS.VIEW, title: 'Bug Reports' }
  },
  {
    path: '/admin/backup',
    name: 'BackupManagement',
    component: () => import('@/views/admin/BackupManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.BACKUPS.VIEW, title: 'Backup & Restore' }
  },
  {
    path: '/admin/recover-data',
    name: 'RecoverData',
    component: () => import('@/views/admin/RecoverDataView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SYSTEM.SUPER_ADMIN,
      title: 'Recover Data',
    },
  },
  {
    path: '/admin/web-status-options',
    name: 'WebStatusOptions',
    component: () => import('@/views/admin/WebStatusOptionsView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.SYSTEM.SUPER_ADMIN,
      title: 'PIM Statuses',
    },
  },
  {
    path: '/admin/database-import-export',
    name: 'DatabaseImportExport',
    component: () => import('@/views/admin/DatabaseImportExportManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.DATABASE_IMPORT_EXPORT.VIEW, title: 'Database Import and Export Management' }
  },
  {
    path: '/admin/roles',
    name: 'RoleManagement',
    component: () => import('@/views/admin/RoleManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.ROLES.VIEW, title: 'Role Management' }
  },
  {
    path: '/admin/navigation',
    name: 'AdminNavigation',
    component: () => import('@/views/admin/NavigationManagementView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermission: PERMISSIONS.ADMIN.NAVIGATION.VIEW,
      title: 'Navigation Management',
    },
  },
  {
    path: '/admin/teams',
    name: 'TeamsManagement',
    component: () => import('@/views/admin/TeamsManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.TEAMS.VIEW, title: 'Teams Management' }
  },
  {
    path: '/admin/teams/:uuid',
    name: 'TeamDetails',
    component: () => import('@/views/admin/TeamDetailsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.TEAMS.VIEW, title: 'Team Details' }
  },
  {
    path: '/admin/sales-reps',
    name: 'SalesRepsManagement',
    component: () => import('@/views/admin/SalesRepsManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.SALESREPS.VIEW, title: 'Sales Reps Management' }
  },
  {
    path: '/admin/external-data',
    name: 'ExternalDataManagement',
    component: () => import('@/views/admin/ExternalDataManagementView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.EXTERNALDATA.VIEW, title: 'External Data Management' }
  },
  {
    path: '/admin/controls',
    name: 'AdminControls',
    component: () => import('@/views/admin/ControlsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.CONTROLS.VIEW, title: 'Controls' }
  },
  {
    path: '/admin/infigo-controls',
    name: 'AdminInfigoControls',
    component: () => import('@/views/admin/InfigoControlsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.INFIGO_CONTROLS.VIEW, title: 'Infigo Controls' }
  },
  {
    // NOTIF-009 — Admin → App Settings: values admins change without a deploy (the server's registry).
    path: '/admin/app-settings',
    name: 'AdminAppSettings',
    component: () => import('@/views/admin/AppSettingsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.APP_SETTINGS.VIEW, title: 'App Settings' }
  },
  {
    path: '/admin/logs',
    name: 'LogsOverview',
    component: () => import('@/views/admin/LogsOverviewView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.LOGS.VIEW, title: 'System Logs' }
  },
  {
    path: '/admin/logs/impersonation',
    name: 'ImpersonationLogs',
    component: () => import('@/views/admin/ImpersonationLogsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.LOGS.VIEW, title: 'Impersonation Logs' }
  },
  {
    path: '/admin/logs/impersonation/:sessionUuid',
    name: 'ImpersonationSessionDetail',
    component: () => import('@/views/admin/ImpersonationSessionDetailView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.LOGS.VIEW, title: 'Impersonation Session' }
  },
  {
    path: '/admin/logs/hubspot',
    name: 'HubSpotIntegrationLogs',
    component: () => import('@/views/admin/HubSpotIntegrationLogsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.LOGS.VIEW, title: 'HubSpot Integration Logs' }
  },
  {
    path: '/admin/api-keys',
    name: 'AdminApiKeys',
    component: () => import('@/views/admin/ApiKeysView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.API_KEYS.VIEW, title: 'API / Webhook Keys' }
  },
  {
    path: '/admin/innovator-apps',
    name: 'AdminInnovatorApps',
    component: () => import('@/views/admin/InnovatorAppsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.INNOVATOR_APPS.VIEW, title: 'Innovator Apps' }
  },
  {
    path: '/admin/logs/:module',
    name: 'ModuleLogs',
    component: () => import('@/views/admin/ModuleLogsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ADMIN.LOGS.VIEW, title: 'Module Logs' }
  },
  
  // Settings Routes
  {
    path: '/settings/profile',
    name: 'SettingsProfile',
    component: () => import('@/views/profile/settings/ProfileView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Profile Settings' } // Personal route - no permission required
  },
  {
    path: '/settings/password',
    name: 'SettingsPassword',
    component: () => import('@/views/profile/settings/PasswordView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Password Settings' } // Personal route - no permission required
  },
  {
    path: '/settings/two-factor',
    name: 'SettingsTwoFactor',
    component: () => import('@/views/profile/settings/TwoFactorAuth.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Two-Factor Authentication' } // Personal route - no permission required
  },
  {
    path: '/settings/timezone',
    name: 'SettingsTimezone',
    component: () => import('@/views/profile/settings/TimezoneView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Timezone Settings' } // Personal route - no permission required
  },
  {
    path: '/settings/notifications',
    name: 'SettingsNotifications',
    component: () => import('@/views/profile/settings/NotificationSettingsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Notification Settings' } // Personal route - no permission required
  },
  {
    // NOTIF-007 — the signed-in user's own notifications, all of them, with filters.
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/notifications/NotificationsView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Notifications' } // Personal route - no permission required
  },
  {
    // NOTIF-008 — a notification's own link (the mails): checks the record, then opens its page.
    path: '/notifications/:uuid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})',
    name: 'NotificationOpen',
    component: () => import('@/views/notifications/NotificationOpenView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Notification' } // Personal route - no permission required
  },
  {
    path: '/settings/remembered-devices',
    name: 'SettingsRememberedDevices',
    component: () => import('../views/profile/settings/RememberedDevicesView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Remembered Devices' }
  },
  {
    path: '/settings',
    redirect: '/settings/profile'
  },
  
  // Other routes
  {
    path: '/notes',
    name: 'Notes',
    component: () => import('@/views/notes/NotesView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Notes' } // Personal route - no permission required
  },
  {
    path: '/roadmap',
    name: 'Roadmap',
    component: () => import('@/views/RoadmapView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Development Roadmap' }
  },
  
  // Site Survey Routes (under Install)
  {
    path: '/surveys',
    name: 'Surveys',
    component: () => import('@/views/surveys/SurveysView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SURVEYS.VIEW, title: 'Site Survey' }
  },
  {
    path: '/surveys/dashboard',
    name: 'SurveyDashboard',
    component: () => import('@/views/surveys/SurveyDashboardView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SURVEYS.VIEW, title: 'Survey Dashboard' }
  },
  {
    path: '/surveys/archive',
    name: 'ArchiveSurveys',
    component: () => import('@/views/surveys/ArchiveSurveysView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SURVEYS.VIEW, title: 'Archive Surveys' }
  },
  {
    path: '/surveys/:uuid',
    name: 'SurveyDetails',
    component: () => import('@/views/surveys/SurveyDetailView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SURVEYS.VIEW, title: 'Survey Details' }
  },
  {
    path: '/surveys/:uuid/edit',
    name: 'EditSurvey',
    component: () => import('@/views/surveys/SurveyEditView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.SURVEYS.EDIT, title: 'Edit Survey' }
  },
  {
    path: '/inquiries',
    name: 'Inquiries',
    component: () => import('@/views/inquiries/InquiriesView.vue'),
    meta: { 
      layout: 'sidebar',
      requiresAuth: true, 
      requiredPermissions: [
        PERMISSIONS.INQUIRIES.VIEW,        // surveys.inquiries.view (for Amazon)
        PERMISSIONS.SALES.INQUIRIES.VIEW,  // sales.inquiries.view (for Local/Walmart)
        PERMISSIONS.SALES.INQUIRIES.CREATE // sales.inquiries.create (prefill access)
      ], 
      title: 'Inquiries' 
    }
  },
  {
    path: '/inquiries/:uuid',
    name: 'InquiryDetail',
    component: () => import('@/views/inquiries/InquiryDetailView.vue'),
    meta: { 
      layout: 'sidebar',
      requiresAuth: true, 
      requiredPermissions: [
        PERMISSIONS.INQUIRIES.VIEW,        // surveys.inquiries.view
        PERMISSIONS.SALES.INQUIRIES.VIEW,  // sales.inquiries.view
        PERMISSIONS.INQUIRIES.CREATE,      // surveys.inquiries.create
        PERMISSIONS.SALES.INQUIRIES.CREATE // sales.inquiries.create
      ], 
      title: 'Inquiry Details' 
    }
  },
  {
    path: '/local-inquiries/:uuid',
    name: 'LocalInquiryDetail',
    component: () => import('@/views/inquiries/LocalInquiryDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [
        PERMISSIONS.SALES.INQUIRIES.VIEW,
        PERMISSIONS.SALES.INQUIRIES.CREATE,
        PERMISSIONS.INQUIRIES.CREATE
      ],
      title: 'Local Inquiry Details'
    }
  },
  {
    path: '/walmart-inquiries/:uuid',
    name: 'WalmartInquiryDetail',
    component: () => import('@/views/inquiries/WalmartInquiryDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [
        PERMISSIONS.SALES.INQUIRIES.VIEW,
        PERMISSIONS.SALES.INQUIRIES.CREATE,
        PERMISSIONS.INQUIRIES.CREATE
      ],
      title: 'Walmart Inquiry Details'
    }
  },
  {
    path: '/national-inquiries/:uuid',
    name: 'NationalInquiryDetail',
    component: () => import('@/views/inquiries/NationalInquiryDetailView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [
        PERMISSIONS.SALES.INQUIRIES.VIEW,
        PERMISSIONS.SALES.INQUIRIES.CREATE,
        PERMISSIONS.INQUIRIES.CREATE
      ],
      title: 'National Inquiry Details'
    }
  },
  
  // Public Site Survey Form (no authentication required)
  {
    path: '/surveys/public',
    name: 'PublicSurveyForm',
    component: () => import('@/views/surveys/PublicSurveyFormView.vue'),
    meta: { title: 'Site Survey Form' }
  },
  {
    path: '/inquiries/public',
    name: 'PublicInquiryForm',
    component: () => import('@/views/inquiries/PublicInquiryFormView.vue'),
    meta: { title: 'Public Inquiry Form' }
  },
  {
    path: '/inquiries/public/local',
    name: 'LocalInquiryForm',
    component: () => import('@/views/inquiries/LocalInquiryFormView.vue'),
    meta: { title: 'Local Inquiry Form' }
  },
  {
    path: '/inquiries/public/walmart',
    name: 'WalmartInquiryForm',
    component: () => import('@/views/inquiries/WalmartInquiryFormView.vue'),
    meta: { title: 'Walmart Inquiry Form' }
  },
  {
    path: '/inquiries/public/national',
    name: 'NationalInquiryForm',
    component: () => import('@/views/inquiries/NationalInquiryFormView.vue'),
    meta: { title: 'National Inquiry Form' }
  },
  {
    path: '/inquiry-form/complete/:type/:prefillUuid',
    name: 'InquiryFormComplete',
    component: () => import('@/views/inquiries/InquiryFormCompleteView.vue'),
    meta: { title: 'Complete Inquiry Form' }
  },

  // Public Design Job Approval (no authentication required)
  {
    path: '/design-jobs/approve/sales/:jobUuid',
    name: 'PublicDesignJobApprovalSales',
    component: () => import('@/views/design-jobs/PublicDesignJobApprovalView.vue'),
    meta: { title: 'Design Job Approval' }
  },
  {
    path: '/design-jobs/approve/queue/:jobUuid',
    name: 'PublicDesignJobApprovalQueue',
    component: () => import('@/views/design-jobs/PublicDesignJobApprovalView.vue'),
    meta: { title: 'Design Job Approval' }
  },
  {
    path: '/design-jobs/approve/customer/:jobUuid',
    name: 'PublicDesignJobApprovalCustomer',
    component: () => import('@/views/design-jobs/PublicDesignJobApprovalView.vue'),
    meta: { title: 'Design Job Approval' }
  },
  
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Profile' } // Personal route - no permission required
  },
  
  // Visual Parity — design system catalog (public, mock data only)
  {
    path: '/visual-parity',
    name: 'VisualParity',
    component: () => import('@/playground/visual-parity/VisualParityView.vue'),
    meta: { title: 'Visual Parity' }
  },

  // Testing/Playground routes (no auth required)
  {
    path: '/tailwind-test',
    name: 'TailwindTest',
    component: () => import('@/views/TailwindTestView.vue'),
    meta: { title: 'Tailwind Test' }
  },
  {
    path: '/api-test',
    name: 'ApiTest',
    component: () => import('@/views/ApiTestView.vue'),
    meta: { title: 'API Test' }
  },
  {
    path: '/layout-clean-demo',
    name: 'LayoutCleanDemo',
    component: () => import('@/playground/LayoutCleanDemo.vue'),
    meta: { title: 'Layout Clean Demo' }
  },
  
  // Super Search
  // Note: User must have at least ONE of these view permissions to access Super Search
  // Controller enforces permissions per entity (each entity requires its specific *.view permission)
  {
    path: '/supersearch',
    name: 'supersearch',
    component: () => import('@/views/supersearch/SupersearchView.vue'),
    meta: {
      layout: 'sidebar',
      requiresAuth: true,
      requiredPermissions: [
        PERMISSIONS.PIM.PRODUCTS.VIEW,
        PERMISSIONS.CRM.ACCOUNTS.VIEW,
        PERMISSIONS.CRM.SUBACCOUNTS.VIEW,
        PERMISSIONS.CRM.TASKS.VIEW,
        PERMISSIONS.CRM.PROJECTS.VIEW,
        PERMISSIONS.CRM.ADDRESSES.VIEW,
        PERMISSIONS.CRM.NOTES.VIEW,
        PERMISSIONS.ADMIN.USERS.VIEW,
        PERMISSIONS.ADMIN.BUGREPORTS.VIEW,
        PERMISSIONS.OPERATIONS.JOBS.VIEW,
        PERMISSIONS.SALESHISTORY.VIEW,
        PERMISSIONS.DESIGN.QUEUE.VIEW,
        PERMISSIONS.DESIGN.COMPLETED.VIEW,
        PERMISSIONS.SYSTEM.SUPER_ADMIN
      ],
      title: 'Super Search'
    }
  },
  
  // Order Entry Routes
  {
    path: '/order-entry/queue',
    name: 'OrderEntryQueue',
    component: () => import('@/views/order-entry/OrderEntryQueueView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ORDER_ENTRY.QUEUE.VIEW, title: 'Order Entry Queue' }
  },
  {
    path: '/order-entry/processed',
    name: 'OrderEntryProcessed',
    component: () => import('@/views/order-entry/OrderEntryProcessedView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ORDER_ENTRY.ORDER.VIEW_PROCESSED, title: 'Processed Orders' }
  },
  {
    path: '/order-entry/declined',
    name: 'OrderEntryDeclined',
    component: () => import('@/views/order-entry/OrderEntryDeclinedView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ORDER_ENTRY.ORDER.VIEW_DECLINED, title: 'Declined Orders' }
  },
  {
    path: '/order-entry/create',
    name: 'OrderEntryCreate',
    component: () => import('@/views/order-entry/OrderEntryCreateView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, requiredPermission: PERMISSIONS.ORDER_ENTRY.ORDER.CREATE, title: 'Create Order' }
  },
  {
    path: '/order-entry/orders/:uuid',
    name: 'OrderEntryDetail',
    component: () => import('@/views/order-entry/OrderEntryDetailView.vue'),
    meta: { layout: 'sidebar', requiresAuth: true, personal: true, title: 'Order Details' }
  },
  {
    path: '/order-entry',
    redirect: '/order-entry/queue'
  },
  
  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// DEPLOY-FRESHNESS-001 — registered before the auth guard: while a new build is live,
// the next navigation is a full page load (nothing to authorize on the old bundle).
installReleaseNavigationGuard(router)

// Navigation guards for authentication
router.beforeEach(async (to, from, next) => {
  try {
    chunkRecoveryPendingFullPath = to.fullPath

    const SALES_PAGE_GUARD_RESERVED = new Set(['dashboard', 'sales-history', 'flash-reports', 'trends'])
    const salesPageMatch = to.path.match(/^\/sales\/([^/]+)$/)
    if (salesPageMatch && !SALES_PAGE_GUARD_RESERVED.has(salesPageMatch[1]) && !isDashboardPageVisible(salesPageMatch[1])) {
      const { useAuthStore: loadAuthStore } = await import('@/stores/auth')
      const authStore = loadAuthStore()
      const routeName = getFirstAccessibleSalesDashboardSubPageRouteName((permission) =>
        authStore.hasPermission(permission)
      )
      if (routeName) {
        next({ name: routeName })
        return
      }
      next(getFirstAccessibleSalesRoute((permission) => authStore.hasPermission(permission)) ?? '/home')
      return
    }

    if (to.path === '/sales' || to.path === '/sales/') {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        const target =
          getFirstAccessibleSalesRoute((permission) => authStore.hasPermission(permission)) ?? '/home'
        next(target)
        return
      }
    }

    // Quick token check for initial authentication status
    const token = localStorage.getItem('auth_token')
    const isAuthenticated = !!token
    
    // Check if route requires authentication
    const requiresAuth = to.matched.some((record: any) => record.meta.requiresAuth)
    
    // Public routes that don't need auth
    const publicRoutes = ['/login', '/register', '/two-factor-challenge', '/forgot-password', '/reset-password', '/', '/visual-parity', '/tailwind-test', '/api-test', '/layout-clean-demo', '/public/vendors/enroll', '/privacy-policy', '/terms-and-conditions']
    const isPublicRoute = publicRoutes.includes(to.path) || 
      to.path.startsWith('/design-jobs/approve') || 
      to.name === 'NotFound'
    
    // Settings routes - allow access without 2FA check
    const isSettingsRoute = to.path.startsWith('/settings')
    
    if (requiresAuth && !isAuthenticated) {
      // Store intended URL for redirect after login
      sessionStorage.setItem('intended_url', to.fullPath)
      // Redirect other guests to welcome page
      next('/')
      return
    }
    
    if ((to.path === '/login' || to.path === '/register' || to.path === '/') && isAuthenticated) {
      // Redirect to home welcome page if already authenticated
      next('/home')
      return
    }
    
    // Check 2FA enforcement and admin access for authenticated users (except settings pages)
    if (isAuthenticated && !isPublicRoute && !isSettingsRoute) {
      try {
        // Import auth store dynamically to avoid circular dependencies
        const { useAuthStore } = await import('@/stores/auth')
        const authStore = useAuthStore()

        // Only refresh user data on initial page load (not on every route navigation).
        // After 2FA verify or leaving any settings page, the store may still reflect pre-refresh
        // localStorage user — refresh before the 2FA gate so setup redirects match the server.
        const leavingSettingsForApp =
          typeof from.path === 'string' &&
          from.path.startsWith('/settings') &&
          typeof to.path === 'string' &&
          !to.path.startsWith('/settings')
        const isInitialLoad =
          !from.name ||
          from.name === 'NotFound' ||
          from.name === 'TwoFactorChallenge' ||
          leavingSettingsForApp

        let skipTwoFactorSetupRedirect = false
        let showSessionDegradedToast = false

        if (isInitialLoad) {
          let refreshOutcome = await authStore.refreshUser()

          if (!refreshOutcome.ok && (refreshOutcome.reason === 'network' || refreshOutcome.reason === 'server')) {
            await new Promise((r) => setTimeout(r, 1000))
            refreshOutcome = await authStore.refreshUser()
          }

          if (!refreshOutcome.ok) {
            if (refreshOutcome.reason === 'timeout') {
              skipTwoFactorSetupRedirect = true
            } else if (refreshOutcome.reason === 'unauthorized') {
              authStore.clearAuth()
              next('/login')
              return
            } else {
              skipTwoFactorSetupRedirect = true
            }
          } else if (!refreshOutcome.user) {
            authStore.clearAuth()
            next('/login')
            return
          }

          showSessionDegradedToast = skipTwoFactorSetupRedirect && !refreshOutcome.ok
        }

        if (showSessionDegradedToast) {
          const k = 'rukh_auth_degraded_toast_at'
          const prev = sessionStorage.getItem(k)
          const now = Date.now()
          if (!prev || now - Number(prev) > 120_000) {
            sessionStorage.setItem(k, String(now))
            void import('@/services/toastService').then(({ toast }) => {
              toast.warning(
                'Could not refresh your profile from the server. You can keep working; we will retry on the next page change.',
                { timeout: 6000 }
              )
            })
          }
        }

        const user = authStore.user as any
        
        // Check explicit permission requirement (permissions-only authorization)
        // Authenticated routes need requiredPermission(s), meta.personal, meta.public, or meta.apiAuthorizes
        // Super admin bypass is handled by hasPermission() checking super.admin
        // Prefer leaf route meta when nested records exist (deepest match wins)
        const routeWithPermission = [...to.matched].reverse().find((record: any) => 
          record.meta?.requiredPermission || record.meta?.requiredPermissions
        )
        const requiredPermission = routeWithPermission?.meta?.requiredPermission as string | undefined
        const requiredPermissions = routeWithPermission?.meta?.requiredPermissions as string[] | undefined
        
        if (requiredPermission) {
          // Route has single permission requirement (legacy aliases accepted for renamed nav gates)
          if (
            !authStore.hasRole('super-admin') &&
            !hasNavigationItemPermission(authStore.hasPermission, requiredPermission)
          ) {
            // Fail-safe: deny access if permission missing (see Section 0.2: Fail-Safe Rule)
            console.error(`Route ${to.path} requires permission ${requiredPermission} but user does not have it. Access denied.`)
            next('/home')
            return
          }
        } else if (requiredPermissions && Array.isArray(requiredPermissions) && requiredPermissions.length > 0) {
          // Route has multiple permission requirements (user needs at least one; aliases accepted)
          if (
            !authStore.hasRole('super-admin') &&
            !hasAnyNavigationItemPermission(authStore.hasPermission, requiredPermissions)
          ) {
            // Fail-safe: deny access if user lacks all required permissions (see Section 0.2: Fail-Safe Rule)
            console.error(`Route ${to.path} requires one of permissions ${requiredPermissions.join(', ')} but user does not have any. Access denied.`)
            next('/home')
            return
          }
        } else if (isWeeklyCheckInModuleNavigation(to)) {
          if (!canAccessWeeklyCheckInModuleFromStore(authStore)) {
            console.error(`Route ${to.path} requires Weekly Check-In module access. Access denied.`)
            next('/home')
            return
          }
        } else if (to.meta.requiresAuth) {
          // Authenticated route without explicit permission - check if it's public or personal
          const isPublic = to.matched.some((record: any) => record.meta.public === true)
          const isPersonal = to.matched.some((record: any) => record.meta.personal === true)
          const apiAuthorizes = to.matched.some((record: any) => record.meta.apiAuthorizes === true)

          if (!isPublic && !isPersonal && !apiAuthorizes) {
            // Fail-safe: deny access if route requires auth but has no permission requirement (see Section 0.2)
            console.error(`Route ${to.path} requires authentication but has no requiredPermission, public, or personal flag. Access denied.`)
            next('/home')
            return
          }
          // Public or personal route - allow access
        }

        // PMO Weekly Check-In: opening another coordinator's route requires view_all (or super-admin), not only self.
        if (to.name === 'WeeklyCheckInCoordinator' && to.params.userUuid) {
          const uid = String(to.params.userUuid)
          const selfUuid = user?.uuid as string | undefined
          if (selfUuid && !uuidEquals(uid, selfUuid)) {
            const canViewOtherCoordinators =
              authStore.hasPermission(PERMISSIONS.PMO.COORDINATOR_FORM.VIEW_ALL) ||
              authStore.hasRole('super-admin') ||
              authStore.hasPermission(PERMISSIONS.SYSTEM.SUPER_ADMIN)
            if (!canViewOtherCoordinators) {
              next('/home')
              return
            }
          }
        }

        const requiresSetupExplicit = user?.requires_2fa_setup === true
        const requiresSetupLegacy =
          user &&
          user.requires_2fa_setup === undefined &&
          !userHasCompletedTwoFactorSetup(user)

        if (!skipTwoFactorSetupRedirect && (requiresSetupExplicit || requiresSetupLegacy)) {
          next('/settings/two-factor')
          return
        }

        next()
      } catch (error) {
        console.error('Error checking auth status:', error)
        // On error, still allow navigation
        next()
        return
      }
    } else {
      next()
    }
  } catch (error) {
    console.error('Error checking authentication:', error)
    // On error, still allow navigation but log the error
    next()
  }
})

// Error handler for navigation errors (including failed chunk loads)
router.onError((error) => {
  console.error('Router navigation error:', error)

  if (isChunkLoadError(error)) {
    const target = chunkRecoveryPendingFullPath || router.currentRoute.value.fullPath
    console.warn('[chunk] Stale chunk after deploy; reloading and restoring route:', target)
    if (!persistChunkRecoveryTargetAndReload(target)) {
      queueMicrotask(() => {
        showChunkRecoveryPendingToastIfAny()
      })
    }
  }
})

// Title management - set page title on route change
router.afterEach((to, from) => {
  chunkRecoveryPendingFullPath = null

  syncDocumentThemeFromStorage()

  // Synchronous so shared filter state (e.g. lifting a drill-applied rep filter)
  // is settled before the destination page mounts and fetches data.
  handleSalesDashboardNavigation(to.name, from.name)

  // DD-FRESHNESS-001: leaving the D&D module marks intake stale so the next entry refetches.
  // Intra-module hops (Queue→Cancelled→History) keep intakeLoaded and do not spam.
  const fromDd =
    from.path === '/design-development' || from.path.startsWith('/design-development/')
  const toDd = to.path === '/design-development' || to.path.startsWith('/design-development/')
  if (fromDd && !toDd) {
    try {
      useDdQueueStore().invalidateQueue()
    } catch {
      /* Pinia may be unavailable during early router boot */
    }
  }

  // Dashboard Pages stub: derive the title from the :page slug so the tab title
  // matches the page name (e.g. "Teams & Reps") rather than the static meta title.
  if (to.name === 'SalesDashboardPageStub' && to.params.page) {
    setDocumentTitle(dashboardPageTitle(String(to.params.page)))
    return
  }

  const title = to.meta?.title as string | undefined

  if (title) {
    setDocumentTitle(title)
  } else {
    const routeName = to.name as string | undefined
    if (routeName) {
      const formattedName = formatRouteName(routeName)
      setDocumentTitle(formattedName)
    } else {
      setDocumentTitle('Dashboard')
    }
  }
})

export default router
